// @ts-ignore
import NumberEasing from "react-number-easing";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, ConditionalWrapper, formatKg, kgSuffix } from "@/lib/utils";
import { ChevronsDown, ChevronsUp } from "lucide-react";
import * as React from "react";

import ThaloIcon from "@/components/thalo-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { LoadingCard } from "@/components/loading-card";
import { DateRange } from "@/dateRanges/dateRanges";
import { Fragment } from "react";
import formatValueAndUnit from "@/metrics/formatValueAndUnit";

export interface DatumCardProps {
  className?: string;
  contentClassName?: string;
  datum?: number;
  title?: string;
  text?: React.ReactNode | string;
  icon?: any;
  suffix?: string;
  forceLoading?: boolean;
  hideIfNoData?: boolean;
  decimalPoints?: number;
  tooltipText?: string;
  hideIfZero?: boolean;
  showDateRange?: DateRange;
  diff?: {
    value: number;
    name: string;
  };
  rightSideComponent?: JSX.Element;
  suffixWrapper?: (children: JSX.Element) => JSX.Element;
}

const DatumTooltip = ({
                        text,
                        children,
                        triggerClassname,
                        className,
                        side = "top",
                        align = "center",
                      }: {
  text: string;
  children: JSX.Element;
  className?: string,
  triggerClassname?: string;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
}) => {
  return (
    <div className={className}>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger className={triggerClassname}>
            {children}
          </TooltipTrigger>
          <TooltipContent side={side} align={align}>
            {text}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export const DatumCard = (
  {
    hideIfNoData = true,
    hideIfZero = false,
    datum,
    suffix,
    diff,
    ...props
  }: DatumCardProps
) => {
  const numberRenderer = React.useCallback((currentRenderValue: number) => {
    if (currentRenderValue === undefined || datum === undefined) return "NaN";
    const decimals = !!props.decimalPoints ? props.decimalPoints :
      (suffix?.toLowerCase().includes("kg") || datum < 100
        ? 1
        : 0);
    return currentRenderValue.toLocaleString(undefined, {
      maximumFractionDigits: decimals,
    });
  }, [datum]);

  // If forceLoading but we have cached data, show refetching state
  const isRefetching = props.forceLoading && datum !== undefined;

  // If forceLoading and no cached data, show full loading card
  if (props.forceLoading && datum === undefined)
    return (
      <LoadingCard className={props.className}/>
    );

  if (datum === undefined && hideIfNoData) return null;
  if (datum === 0 && hideIfZero) return null;

  if (datum === undefined) return <LoadingCard className={props.className}/>;

  return (
    <ConditionalWrapper
      condition={!!props.tooltipText}
      wrapper={(children) => (
        <DatumTooltip
          text={props.tooltipText || ""}
          className={cn("col-span-12 lg:col-span-4 flex flex-col justify-center text-left", props.className)}
        >
          {children}
        </DatumTooltip>
      )}
    >
      <Card className={cn("p-4 text-left relative overflow-hidden", props.className)}>
        {isRefetching && (
          <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-gradient-to-r from-transparent via-thalo-blue to-transparent animate-shimmer opacity-50"
               style={{
                 backgroundSize: '200% 100%',
                 animation: 'shimmer 5s infinite'
               }}
          />
        )}
        <style>{`
          @keyframes shimmer {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
        <div className="h-full flex justify-between gap-1 items-center"> {/* Creates a 5-column grid */}
          <div className=""> {/* This will take up 4/5 of the space */}
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-medium text-thalo-foreground text-sm truncate">
                {props.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-0">
              <div
                className={cn(
                  "text-2xl xl:text-5xl font-normal text-thalo-foreground flex gap-1 items-baseline",
                  props.contentClassName
                )}
              >
                {
                  !!suffix ? formatValueAndUnit(
                    suffix.toLowerCase() === "kg" ? formatKg(datum) : datum,
                    suffix.toLowerCase() === "kg" ? kgSuffix(datum) : suffix,
                    (value) => (
                      <Fragment key="value">
                        <NumberEasing
                          customFunctionRender={numberRenderer}
                          value={value}
                          speed={1500}
                          ease="quintInOut"
                        />
                      </Fragment>
                    ),
                    (unit) => (
                      <Fragment key="unit">
                        <span className="text-lg xl:text-xl truncate">
                          <ConditionalWrapper condition={!!props.suffixWrapper} wrapper={props.suffixWrapper}>
                            <span>
                              {unit}
                            </span>
                          </ConditionalWrapper>
                        </span>
                      </Fragment>
                    )
                  ) : (
                    <NumberEasing
                      customFunctionRender={numberRenderer}
                      value={datum}
                      speed={1500}
                      ease="quintInOut"
                    />
                  )
                }
              </div>
              <div
                className={`text-sm xl:text-lg font-light text-thalo-foreground flex items-center justify-left ${diff ? "truncate" : ""
                }`}
              >
                {diff ? (
                  <>
                    {diff.value > 0 ? (
                      <ChevronsUp className="mr-0 w-4 xl:w-5" strokeWidth={3}/>
                    ) : (
                      <ChevronsDown className="mr-0 w-4 xl:w-5" strokeWidth={3}/>
                    )}{" "}
                    <NumberEasing
                      value={Math.abs(diff?.value)}
                      speed={1500}
                      decimals={1}
                      ease="quintInOut"
                    />
                    % {diff.value > 0 ? "over" : "below"} {diff.name}
                  </>
                ) : (
                  props.text
                )}
              </div>
            </CardContent>
            {
              props.showDateRange && (
                <div className="text-xs border border-gray-200 rounded-md p-2 w-fit text-gray-500 col-span-full">
                  {props.showDateRange.label}
                </div>
              )
            }
          </div>
          <div className="flex flex-col justify-center items-center"> {/* This will take up 1/5 of the space */}
            {
              props.icon && (
                <ThaloIcon iconName={props.icon} color="bg-white" width="w-12" height="h-12"/>
              )
            }
            {
              props.rightSideComponent && props.rightSideComponent
            }
          </div>
        </div>
      </Card>
    </ConditionalWrapper>
  );
};
